import React, { useRef, useEffect, useState, useCallback } from 'react'
import { LMS_ADMIN_WRITE, SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import KeycloakService from '../../../../utils/keycloakService'
import '../../../administration/admin.scss'
import MedAgDataGrid from '../../../../components/MedAgDataGrid'
import { ColDef, INumberFilterParams } from 'ag-grid-community'
import '../../../lms/lms.scss'
import { IconButton, Switch, useTheme } from '@mui/material'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsFormMode,
  setLmsFormMode,
  setLmsItemId,
} from '../../lmsSlice'
import { toast } from 'react-toastify'
import { getAllLabDevices, toggleEnable } from '../../../../services/LmsService'
import {
  CREATE,
  deviceLocationField,
  deviceLocationKey,
  deviceModelNameField,
  deviceModelNameKey,
  deviceNameField,
  deviceNameKey,
  deviceOperatingUnitField,
  deviceOperatingUnitKey,
  deviceVendorField,
  deviceVendorKey,
  LAB_ANALYZER_TABLE_HEADING,
  labDeviceItem,
  labDeviceSearchItems,
  lmsFormCreateMode,
  lmsFormEditMode,
  lmsFormNoneMode,
} from '../../constants'
import CreateUpdateLabDevice from './CreateUpdateLabDevice'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import { readableDateTimeFormat } from '../../../../utils/dateUtils'
import CustomHeader from '../../../Radiology/components/RadiologyModel/CustomHeader'
import SelectableSearch from '../../../../components/SelectableSearch'

export const styles = () => ({
  columnFontSize: {
    fontSize: '12px',
  },
  enableColumnStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
})

const LabDevices = () => {
  const depTableContainerRef = useRef<HTMLDivElement | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [deviceDeleteItem, setDeviceDeleteItem] = useState<any>({})
  const dispatch = useAppDispatch()
  const mode = useAppSelector(selectedLmsFormMode)
  const [data, setData] = useState([])
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    const resizeHandler = () => {
      const container = depTableContainerRef?.current
      if (container) {
        const availableHeight: any =
          window?.innerHeight - container?.getBoundingClientRect()?.top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window?.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window?.removeEventListener('resize', resizeHandler)
    }
  }, [])

  const handleEnableChange = async (data: any) => {
    const response = await toggleEnable(data)
    if (response?.success) {
      toast?.success(response?.message)
      getDevices(page, pageSize)
    } else {
      toast?.error(response?.message)
    }
  }

  const handleDeleteDialogOpen = (data: any) => {
    setDeleteDialog(true)
    setDeviceDeleteItem(data)
  }

  function handleCloseDeleteDialog() {
    setDeleteDialog(false)
    setDeviceDeleteItem({})
  }

  async function onEdit(id: any) {
    dispatch(setLmsItemId(id))
    dispatch(setLmsFormMode(lmsFormEditMode))
  }

  function handleBackClick() {
    dispatch(setLmsFormMode(lmsFormNoneMode))
  }

  function handleCreateButton() {
    dispatch(setLmsFormMode(lmsFormCreateMode))
  }

  const tableEditColumn = (params: any) => {
    return KeycloakService?.hasRole([LMS_ADMIN_WRITE]) ? (
      <a
        data-testid={'Edit'}
        href='#'
        onClick={() => {
          onEdit(params?.data?.id)
        }}
      >
        {params?.data?.name}
      </a>
    ) : (
      <span>{params?.data?.name}</span>
    )
  }

  const enableColumn = (params: any) => {
    return (
      <div className='LabDeviceTableEnableColumnStyle'>
        <Switch
          checked={params?.data?.enabled}
          onChange={() => handleEnableChange(params?.data)}
        />
      </div>
    )
  }

  const actionColumn = (params: any) => {
    return (
      <IconButton
        title='Delete'
        size='small'
        onClick={() => handleDeleteDialogOpen(params?.data)}
        disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
      >
        <DeleteOutlined fontSize='small' />
      </IconButton>
    )
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'Id',
      field: 'id',
      minWidth: 50,
      sortable: true,
      filter: false,
      headerClass: 'custom-header-cell',
      cellClass: classes?.columnFontSize,
    },
    {
      headerName: 'Device Name',
      field: 'name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: tableEditColumn,
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Device Model',
      field: 'model_name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Equipment Name',
      field: 'equipment_name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Department',
      field: 'department',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Device Type',
      field: 'device_type',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Vendor',
      field: 'vendor',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 110,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Operating Unit',
      field: 'facility',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Location',
      field: 'location',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 110,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Device IP',
      field: 'device_ip',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Internal Port',
      field: 'internal_port',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 100,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'External Port',
      field: 'external_port',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 100,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Mode',
      field: 'default_connection_type',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 110,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Connection Status',
      field: 'status',
      flex: 1,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 120,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Enabled',
      field: 'enabled',
      sortable: true,
      filter: false,
      cellClass: classes?.enableColumnStyle,
      headerClass: 'custom-header-cell',
      cellRenderer: enableColumn,
      minWidth: 110,
    },
    {
      headerName: 'Protocol',
      field: 'protocol',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 110,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Created On',
      field: 'created_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return readableDateTimeFormat(new Date(params?.data?.created_at))
      },
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Updated By',
      field: 'updated_by',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 150,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Updated On',
      field: 'updated_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return readableDateTimeFormat(new Date(params?.data?.updated_at))
      },
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Action',
      sortable: true,
      filter: false,
      cellClass: classes?.enableColumnStyle,
      headerClass: 'custom-header-cell',
      cellRenderer: actionColumn,
      width: 100,
    },
  ]

  const handleSearch = (query: any, param: string) => {
    let parameter = ''
    let value = ''
    switch (query) {
      case deviceNameField:
        parameter = deviceNameKey
        break
      case deviceModelNameField:
        parameter = deviceModelNameKey
        break
      case deviceVendorField:
        parameter = deviceVendorKey
        break
      case deviceOperatingUnitField:
        parameter = deviceOperatingUnitKey
        break
      case deviceLocationField:
        parameter = deviceLocationKey
    }
    value = param
    getDevices(page, pageSize, parameter, value)
  }

  const getDevices = useCallback(
    async (page: number, pageSize: number, param = '', value = '') => {
      const response = await getAllLabDevices(
        page * pageSize,
        pageSize,
        param,
        value
      )
      if (response?.success) {
        const devicesData = response?.data?.data?.map((item: any) => {
          return {
            ...item,
            model_name: item?.device_model?.name,
            equipment_name: item?.device_model?.equipment_name,
            department: item?.device_model?.department,
            device_type: item?.device_model?.device_type,
            vendor: item?.device_model?.vendor,
            protocol: item?.device_model?.protocol,
            default_connection_type:
              item?.device_model?.default_connection_type,
          }
        })
        setData(devicesData)
        setTotalCount(response?.data?.total_records)
      } else {
        setData([])
        toast?.error(response?.message)
      }
    },
    []
  )

  useEffect(() => {
    getDevices(page, pageSize)
  }, [getDevices, mode, page, pageSize])

  return (
    <div className='LisFirstDivStyle'>
      <div className='LisSecondDivStyle'>
        <div className='admin-header-text LisThirdDivStyle'>
          <div>
            {mode !== lmsFormNoneMode && (
              <>
                <IconButton onClick={handleBackClick}>
                  <KeyboardBackspaceIcon className='back-btn-lab' />
                </IconButton>
              </>
            )}
          </div>
          <span>{LAB_ANALYZER_TABLE_HEADING}</span>
        </div>
        {mode === lmsFormNoneMode && (
          <div className='LisSearchCreateButton'>
            <SelectableSearch
              items={labDeviceSearchItems}
              onSearch={handleSearch}
              placeHolder='Search by name, model, vendor, Operating Unit, Location'
            />
            <button className='admin-btn' onClick={handleCreateButton}>
              {CREATE}
            </button>
          </div>
        )}
      </div>
      {mode === lmsFormNoneMode ? (
        <div
          data-testid='lab-device-grid'
          className='LisAgGridStyle'
          ref={depTableContainerRef}
        >
          <MedAgDataGrid
            tableHeight={'100%'}
            rowData={data?.length ? data : []}
            columnDefs={columnDefs}
            pagination={true}
            headerHeight={30}
            searchFieldWidth={200}
            searchField={false}
            exportIcon={false}
            pageSizeOptions={[10, 20, 50, 100]}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRows={totalCount}
          />
          {deleteDialog && (
            <DeleteConfirmationDialog
              openDialog={deleteDialog}
              closeDialog={handleCloseDeleteDialog}
              data={deviceDeleteItem}
              name={deviceDeleteItem?.name}
              setData={setData}
              item={labDeviceItem}
            />
          )}
        </div>
      ) : (
        <CreateUpdateLabDevice />
      )}
    </div>
  )
}

export default LabDevices
